import { XIcon } from "lucide-react";
import React, { SetStateAction, useState, useMemo } from "react";
import { Checkbox } from "../../../shadcn/components/ui/checkbox";
import { Button } from "../../../shadcn/components/ui/button";

interface CustomRejectionStepModelProps {
  handleSubmit: (reason: string, selectedReasons: string[]) => void;
  onClose: React.Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  bidderKycType?: string;
  bidderType?: string;
  paymentValidation?: boolean;
  isSecondaryBidder?: boolean;
  is_joint?: boolean;
}

interface RejectionOption {
  id: string;
  label: string;
  note?: string;
}

export default function CustomRejectionStepModel({
  handleSubmit,
  onClose,
  isLoading,
  bidderKycType = "online",
  bidderType = "individual",
  paymentValidation = false,
  isSecondaryBidder = false,
  is_joint = false,
}: CustomRejectionStepModelProps) {
  const [cancellationReason, setCancellationReason] = useState("");
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>("");

  // Generate rejection options based on bidderKycType and bidderType
  const rejectionOptions = useMemo<RejectionOption[]>(() => {
    // For secondary bidders in online mode, only show KYC Rejection and Bid Application Data Issue
    if (isSecondaryBidder && bidderKycType === "online") {
      return [
        {
          id: "kyc_rejection",
          label: "KYC Rejection",
          note: "Note: If the KYC data is incorrect, the user must sign the bid form again since the data has changed.",
        },
        {
          id: "bid_application_data_issue",
          label: "Bid Application Data Issue",
          note: "Note: If the bid application data is incorrect, the user must sign the bid form again since the data has changed.",
        },
      ];
    }
    // For secondary bidders in offline mode, only show KYC Rejection
    if (isSecondaryBidder && bidderKycType === "offline") {
      return [
        {
          id: "kyc_rejection",
          label: "KYC Rejection",
          note: "Note: If the KYC data is incorrect, the user must download the new bid form and submit it at the bank.",
        },
      ];
    }

    if (bidderType === "individual" && is_joint === false) {
      if (bidderKycType === "online") {
        if (paymentValidation === true) {
          return [
            { id: "emd_rejection", label: "EMD Rejection" },
            {
              id: "kyc_rejection",
              label: "KYC Rejection",
              note: "Note: If the KYC data is incorrect, the user must sign the bid form again since the data has changed.",
            },
          ];
        }
        return [
          {
            id: "kyc_rejection",
            label: "KYC Rejection",
            note: "Note: If the KYC data is incorrect, the user must sign the bid form again since the data has changed.",
          },
        ];
      } else if (bidderKycType === "offline") {
        if (paymentValidation === true) {
          return [
            { id: "emd_rejection", label: "EMD Rejection" },
            { id: "kyc_rejection", label: "KYC Rejection" },
            {
              id: "bid_application_data_issue",
              label: "Bid Application Data Issue",
              note: "Note: If the bid application data is incorrect, the user must download the new bid form and submit it at the bank.",
            },
          ];
        }
        return [
          { id: "kyc_rejection", label: "KYC Rejection" },
          {
            id: "bid_application_data_issue",
            label: "Bid Application Data Issue",
            note: "Note: If the bid application data is incorrect, the user must download the new bid form and submit it at the bank.",
          },
        ];
      }
    } else if (bidderType === "individual" && is_joint === true) {
      if (bidderKycType === "online") {
        if (paymentValidation === true) {
          return [
            { id: "emd_rejection", label: "EMD Rejection" },
            {
              id: "kyc_rejection",
              label: "KYC Rejection",
              note: "Note: If the KYC data is incorrect, the user must sign the bid form again since the data has changed.",
            },
            {
              id: "bid_application_data_issue",
              label: "Bid Application Data Issue",
              note: "Note: If the bid application data is incorrect, the user must sign the bid form again since the data has changed.",
            },
          ];
        }
        return [
          {
            id: "kyc_rejection",
            label: "KYC Rejection",
            note: "Note: If the KYC data is incorrect, the user must sign the bid form again since the data has changed.",
          },
          {
            id: "bid_application_data_issue",
            label: "Bid Application Data Issue",
            note: "Note: If the bid application data is incorrect, the user must sign the bid form again since the data has changed.",
          },
        ];
      } else if (bidderKycType === "offline") {
        if (paymentValidation === true) {
          return [
            { id: "emd_rejection", label: "EMD Rejection" },
            { id: "kyc_rejection", label: "KYC Rejection" },
            {
              id: "bid_application_data_issue",
              label: "Bid Application Data Issue",
              note: "Note: If the bid application data is incorrect, the user must download the new bid form and submit it at the bank.",
            },
          ];
        } else {
          return [
            { id: "kyc_rejection", label: "KYC Rejection" },
            { id: "joint_proof_rejection", label: "Joint Proof Rejection" },
            {
              id: "bid_application_data_issue",
              label: "Bid Application Data Issue",
              note: "Note: If the bid application data is incorrect, the user must download the new bid form and submit it at the bank.",
            },
          ];
        }
      }
    } else {
      if (bidderKycType === "online") {
        if (paymentValidation === true) {
          return [
            { id: "emd_rejection", label: "EMD Rejection" },
            {
              id: "business_entity_rejection",
              label: "Business Entity Rejection",
              note: "Note: If the business entity data is incorrect, the user must sign the bid form again since the data has changed.",
            },
            {
              id: "add_ar_rejection",
              label: "AR Details Rejection",
              note: "Note: If the KYC data is incorrect, the user must sign the bid form again since the data has changed.",
            },
            {
              id: "bid_application_data_issue",
              label: "Bid Application Data Issue",
              note: "Note: If the bid application data is incorrect, the user must sign the bid form again since the data has changed.",
            },
          ];
        }
        return [
          {
            id: "business_entity_rejection",
            label: "Business Entity Rejection",
            note: "Note: If the business entity data is incorrect, the user must sign the bid form again since the data has changed.",
          },
          {
            id: "add_ar_rejection",
            label: "AR Details Rejection",
            note: "Note: If the KYC data is incorrect, the user must sign the bid form again since the data has changed.",
          },
          {
            id: "bid_application_data_issue",
            label: "Bid Application Data Issue",
            note: "Note: If the bid application data is incorrect, the user must sign the bid form again since the data has changed.",
          },
        ];
      } else if (bidderKycType === "offline") {
        if (paymentValidation === true) {
          return [
            { id: "emd_rejection", label: "EMD Rejection" },
            {
              id: "business_entity_rejection",
              label: "Business Entity Rejection",
              note: "Note: If the business entity data is incorrect, the user must download the new bid form and submit it at the bank.",
            },
            {
              id: "add_ar_rejection",
              label: "AR Details Rejection",
              note: "Note: If the KYC data is incorrect, the user must download the new bid form and submit it at the bank.",
            },
            {
              id: "bid_application_data_issue",
              label: "Bid Application Data Issue",
              note: "Note: If the bid application data is incorrect, the user must download the new bid form and submit it at the bank.",
            },
          ];
        }
        return [
          {
            id: "emd_rejection",
            label: "Business Entity Rejection",
            note: "Note: If the business entity data is incorrect, the user must download the new bid form and submit it at the bank.",
          },

          {
            id: "business_entity_rejection",
            label: "Business Entity Rejection",
            note: "Note: If the business entity data is incorrect, the user must download the new bid form and submit it at the bank.",
          },
          {
            id: "add_ar_rejection",
            label: "AR Details Rejection",
            note: "Note: If the KYC data is incorrect, the user must download the new bid form and submit it at the bank.",
          },
          {
            id: "bid_application_data_issue",
            label: "Bid Application Data Issue",
            note: "Note: If the bid application data is incorrect, the user must download the new bid form and submit it at the bank.",
          },
        ];
      }
    }
    return [];
  }, [bidderKycType, bidderType, isSecondaryBidder, paymentValidation]);

  const handleCheckboxChange = (optionId: string, checked: boolean) => {
    if (checked) {
      setSelectedReasons([...selectedReasons, optionId]);
    } else {
      setSelectedReasons(selectedReasons.filter((id) => id !== optionId));
    }
  };

  const handleRadioChange = (optionId: string) => {
    setSelectedReason(optionId);
  };
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
      <div className="bg-white shadow-2xl  w-full max-w-[300px] mobile:max-w-[380px] md:max-w-lg p-6 max-h-screen">
        <div className="w-full flex justify-between items-center  pb-3">
          <p className="font-openSans font-semibold text-web-body-lg leading-5 text-brand-bgBlack">
            Reject Bidder Request
          </p>
          <button
            onClick={() => {
              onClose(false);
            }}
            className="text-gray-500 bg-brand-bgLightGrey rounded-full p-1.5 hover:text-gray-700"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        <div>
          <p className="font-openSans text-xs text-brand-textGrey">
            Please select the appropriate KYC re-attempt step for the user from
            the options below.
            <br />
          </p>
        </div>
        <div className="my-4 space-y-3">
          {rejectionOptions.map((option) => (
            <div key={option.id} className="flex flex-col gap-1">
              <label className="flex items-center gap-2 cursor-pointer">
                {isSecondaryBidder ? (
                  <input
                    type="radio"
                    id={option.id}
                    name="rejection-reason"
                    checked={selectedReason === option.id}
                    onChange={() => handleRadioChange(option.id)}
                    className="w-4 h-4 text-brand-orange border-gray-300 focus:ring-brand-orange focus:ring-2"
                  />
                ) : (
                  <Checkbox
                    id={option.id}
                    checked={selectedReasons.includes(option.id)}
                    onCheckedChange={(checked: boolean) => {
                      handleCheckboxChange(option.id, checked as boolean);
                    }}
                    className="data-[state=checked]:bg-brand-orange data-[state=checked]:border-brand-orange"
                  />
                )}
                <span className="font-openSans font-normal text-web-body-sm leading-4 text-brand-textGrey">
                  {option.label}
                </span>
              </label>
              {option.note &&
                ((isSecondaryBidder && selectedReason === option.id) ||
                  (!isSecondaryBidder &&
                    selectedReasons.includes(option.id))) && (
                  <p className="font-openSans text-xs text-brand-orange ml-6 italic">
                    {option.note}
                  </p>
                )}
            </div>
          ))}
        </div>

        <p className="font-openSans text-xs text-brand-textGrey">
          {" "}
          Please Ensure you provide a clear rejection reason so the bidder can
          correct the information before reapplying.{" "}
        </p>
        <div className={` relative w-full mt-5`}>
          <textarea
            id="name"
            value={cancellationReason}
            onChange={(e: any) => {
              setCancellationReason(e.target.value);
            }}
            className={`bg-white   py-4 peer pl-4 autofill:bg-white focus:border-brand-orange focus:outline-none focus:ring-0 pb-[6px] min-h-[120px]  outline-none  border border-[#EEEEEE]  w-full   font-openSans font-normal  leading-4 text-brand-textLightGrey `}
          />
          <label
            htmlFor="name"
            className={`absolute font-openSans font-normal text-web-body-sm leading-3 mid:text-web-body-lg mid:leading-5 ${
              !cancellationReason
                ? "text-brand-borderGrey"
                : "text-transparent hidden"
            } left-4 top-4 pointer-events-none transition-all duration-300 peer-placeholder-shown:top-1.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-[10px] peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:leading-[13px] peer-focus:text-brand-orange peer-valid:top-1.5 peer-valid:text-[10px] peer-valid:leading-[13px] peer-valid:text-brand-borderGrey`}
          >
            Reason For Rejecting Request
          </label>
        </div>

        <div className="w-full flex justify-center mt-6">
          <Button
            disabled={
              isLoading ||
              cancellationReason === "" ||
              (isSecondaryBidder
                ? selectedReason === ""
                : selectedReasons.length === 0)
            }
            onClick={() => {
              if (isSecondaryBidder) {
                handleSubmit(cancellationReason, [selectedReason]);
              } else {
                handleSubmit(cancellationReason, selectedReasons);
              }
            }}
            className="w-full max-w-sm"
          >
            Reattempt
          </Button>
        </div>
      </div>
    </div>
  );
}
